import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const STATS = [
  { value: 27, suffix: '+', label: 'Years of Excellence' },
  { value: 1600, suffix: '+', label: 'Students Enrolled' },
  { value: 3, suffix: '', label: 'Campuses' },
  { value: 120, suffix: '+', label: 'Expert Faculty' },
];

const useCountUp = (target, duration = 1800, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const StatItem = ({ value, suffix, label, animate }) => {
  const count = useCountUp(value, 1800, animate);
  return (
    <div className="text-center">
      <div className="font-poppins font-800 text-3xl sm:text-4xl text-white leading-none">
        {animate ? count : 0}{suffix}
      </div>
      <div className="text-white/60 text-xs sm:text-sm mt-1 font-inter">{label}</div>
    </div>
  );
};

const HeroBanner = () => {
  const [videoError, setVideoError] = useState(false);
  const [animateStats, setAnimateStats] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimateStats(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" aria-label="Hero Banner">
      {/* Background */}
      <div className="absolute inset-0 -z-20">
        {!videoError ? (
          <video
            className="w-full h-full object-cover object-center"
            autoPlay muted loop playsInline
            onError={() => setVideoError(true)}
            poster="/assets/hero-banner.jpg"
          >
            <source
              src="./admin/api/wp-content/uploads/2024/04/WhatsApp-Video-2024-04-01-at-9.09.58-AM.mp4"
              type="video/mp4"
            />
          </video>
        ) : (
          <img
            src="/assets/hero-banner.jpg"
            alt="PAK School Campus"
            className="w-full h-full object-cover object-center"
          />
        )}
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-navy/85 via-navy/60 to-navy/75" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />

      {/* Decorative orbs */}
      <div className="absolute top-1/4 right-1/4 w-72 h-72 rounded-full bg-primary/10 blur-3xl -z-10 animate-pulse2" />
      <div className="absolute bottom-1/3 left-1/4 w-56 h-56 rounded-full bg-accent/10 blur-3xl -z-10 animate-pulse2 delay-700" />

      {/* Content */}
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="max-w-3xl">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 anim-init animate-fade-up delay-100">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white text-sm font-inter font-500">Excellence in Education Since 1998</span>
          </div>

          {/* Heading */}
          <h1 className="font-poppins font-900 text-white mb-4 leading-none anim-init animate-fade-up delay-200"
            style={{ fontSize: 'clamp(36px, 6vw, 72px)' }}>
            Welcome to{' '}
            <span className="relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                PAK School
              </span>
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-primary">
              &amp; College
            </span>
          </h1>

          {/* Tagline */}
          <p className="font-poppins font-600 text-white/90 text-xl sm:text-2xl mb-6 anim-init animate-fade-up delay-300">
            Every Student A Star ⭐
          </p>

          {/* Description */}
          <p className="font-inter text-white/70 text-base sm:text-lg leading-relaxed mb-10 max-w-xl anim-init animate-fade-up delay-400">
            Nurturing academic excellence, building character, and shaping future leaders through innovative teaching and a comprehensive curriculum in Mardan.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 anim-init animate-fade-up delay-500">
            <Link
              to="/admission"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary hover:bg-primary-dark text-white font-poppins font-600 text-sm rounded-full transition-all duration-200 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 group"
            >
              Apply Now
              <i className="fas fa-arrow-right transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 text-white font-poppins font-600 text-sm rounded-full border border-white/30 hover:border-white/50 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5"
            >
              <i className="fas fa-play-circle" />
              Discover More
            </Link>
          </div>
        </div>

        {/* Stats bar */}
        <div
          ref={statsRef}
          className="mt-16 sm:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 anim-init animate-fade-up delay-600"
        >
          {STATS.map((stat, i) => (
            <div key={i} className="relative">
              {i > 0 && (
                <div className="hidden sm:block absolute left-0 top-1/2 -translate-y-1/2 w-px h-10 bg-white/20" />
              )}
              <StatItem {...stat} animate={animateStats} />
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>
        <span className="text-white text-xs font-inter tracking-widest uppercase">Scroll</span>
        <div className="w-5 h-8 border-2 border-white/40 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-white rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
